import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { resolveVioAssetUrl } from '../utils/vioAssetUrl';

function parseBalanceBigInt(s) {
  try {
    if (s == null || s === '') return 0n;
    const w = String(s).trim().split(/[.eE]/)[0];
    return BigInt(w || '0');
  } catch {
    return 0n;
  }
}

/** Sendable company balance: prefer effectiveCompanyBalance (non-expired lots), else ledger companyBalance. Omit both = do not block (older API). */
function sendableCompanyBalance(token) {
  return token.effectiveCompanyBalance ?? token.companyBalance;
}

function isTokenSelectable(token) {
  const b = sendableCompanyBalance(token);
  if (b === undefined || b === null) return true;
  return parseBalanceBigInt(b) > 0n;
}

function TokenModal({ isOpen, onClose, onApply, initialToken }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [amount, setAmount] = useState('');
  const [search, setSearch] = useState('');

  const handleClose = useCallback(() => {
    setSearch('');
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      return;
    }
    setSelectedId(initialToken?.tokenId || null);
    setAmount(initialToken?.tokenAmount?.toString() || '');
    setSearch('');
    fetchTokens();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const res = await api.get('/vio/tokens', {
        params: { page: 1, limit: 100, isActive: true },
      });
      if (res.data.success) {
        setTokens(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch tokens:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || loading || !selectedId) return;
    const t = tokens.find((x) => x._id === selectedId);
    if (!t || !isTokenSelectable(t)) setSelectedId(null);
  }, [isOpen, loading, tokens, selectedId]);

  const handleConfirm = () => {
    const token = tokens.find((t) => t._id === selectedId);
    if (!token || !isTokenSelectable(token)) return;
    const n = parseInt(amount, 10);
    if (!amount || !Number.isFinite(n) || n <= 0) return;
    const cap = sendableCompanyBalance(token);
    if (cap != null && BigInt(n) > parseBalanceBigInt(cap)) return;

    onApply({
      type: 'token',
      tokenId: token._id,
      tokenName: token.name,
      tokenSymbol: token.symbol,
      tokenAmount: n,
      tokenIcon: token.icon || '',
      voucherId: '',
      voucherName: '',
      voucherImage: '',
      voucherValue: 0,
      voucherValueCurrency: '',
      voucherValueType: '',
      voucherType: '',
      voucherCategory: '',
    });
    handleClose();
  };

  const filtered = tokens.filter((t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      t.name?.toLowerCase().includes(q) ||
      t.symbol?.toLowerCase().includes(q)
    );
  });

  if (!isOpen) return null;

  const selectedToken = tokens.find((t) => t._id === selectedId);
  const amtNum = parseInt(amount, 10);
  const maxBalRaw = selectedToken ? sendableCompanyBalance(selectedToken) : null;
  const maxBal =
    selectedToken && maxBalRaw != null ? parseBalanceBigInt(maxBalRaw) : null;
  const overMax =
    maxBal != null && Number.isFinite(amtNum) && amtNum > 0 && BigInt(amtNum) > maxBal;
  const isValid =
    selectedToken &&
    isTokenSelectable(selectedToken) &&
    amount &&
    Number.isFinite(amtNum) &&
    amtNum > 0 &&
    !overMax;

  return createPortal(
    <div className="modal-overlay modal-overlay--nested">
      <div className="modal modal--token-picker" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Select Token</h3>
          <button type="button" className="modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>
        <div className="modal-body modal-body--voucher-picker">
          <div className="voucher-picker-search">
            <label htmlFor="token-picker-search-input" className="sr-only">
              Search tokens by name or symbol
            </label>
            <div className="voucher-picker-search-inner">
              <span className="voucher-picker-search-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </span>
              <input
                id="token-picker-search-input"
                type="search"
                className="voucher-picker-search-input"
                placeholder="Search by token name or symbol…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          {loading ? (
            <div className="voucher-picker-loading">
              <div className="spinner" />
              <span>Loading tokens…</span>
            </div>
          ) : (
            <div className="coupon-list coupon-list--picker">
              {filtered.length === 0 ? (
                <p className="voucher-picker-empty">No tokens match your search.</p>
              ) : (
                filtered.map((token) => {
                  const selectable = isTokenSelectable(token);
                  const iconUrl = resolveVioAssetUrl(token.icon);
                  return (
                    <div
                      key={token._id}
                      role="button"
                      tabIndex={selectable ? 0 : -1}
                      className={`coupon-card${selectedId === token._id ? ' selected' : ''}${selectable ? '' : ' row-disabled'}`}
                      onClick={() => {
                        if (!selectable) return;
                        setSelectedId(token._id);
                      }}
                      onKeyDown={(e) => {
                        if (!selectable) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedId(token._id);
                        }
                      }}
                    >
                      <div className="token-picker-row">
                        <div className="token-picker-thumb-wrap">
                          {iconUrl ? (
                            <img className="token-picker-thumb" src={iconUrl} alt="" />
                          ) : (
                            <div className="token-picker-thumb-placeholder" aria-hidden="true">
                              🪙
                            </div>
                          )}
                        </div>
                        <div className="coupon-info">
                          <div className="coupon-name-row">
                            <strong>{token.name}</strong>
                            {token.symbol ? (
                              <span className="voucher-chip voucher-chip--public">{token.symbol}</span>
                            ) : null}
                          </div>
                          <span className="coupon-meta">
                            Supply: {token.totalSupply?.toLocaleString?.() ?? token.totalSupply ?? '—'}
                            {sendableCompanyBalance(token) != null && (
                              <>
                                {' · '}
                                Available: {String(sendableCompanyBalance(token))}
                              </>
                            )}
                          </span>
                          {!selectable && (
                            <span className="coupon-meta coupon-meta--error">
                              No available company balance for rewards
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {selectedId && selectedToken && (
            <div className="token-picker-amount">
              <label className="form-label" htmlFor="token-picker-amount-input">
                Token amount
              </label>
              <input
                id="token-picker-amount-input"
                className="form-input"
                type="number"
                min="1"
                step="1"
                placeholder="Enter amount (e.g. 10)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              {amount && (!Number.isFinite(amtNum) || amtNum <= 0) && (
                <p className="token-picker-amount-hint">Amount must be greater than 0</p>
              )}
              {overMax && (
                <p className="token-picker-amount-hint">
                  Amount exceeds available balance ({String(sendableCompanyBalance(selectedToken))})
                </p>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer modal-footer--voucher-picker">
          <button
            type="button"
            className="btn btn-primary voucher-picker-confirm"
            disabled={!isValid}
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default TokenModal;
